//UserProfileDialog
//***********************************************************************************************************************
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import * as tokenService from "../../models/services/tokenService";
import * as userService from "../../models/services/userService";
import { SaveDataObj } from "../../framework/saveData";
import { Translations } from "ui/Scripts/source/models/translations";
import { Dialog } from "../framework/dialog";
import { CheckBox } from "ui/Scripts/source/components/framework/checkbox";
import { ToolAddFileButton, ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { CameraPhoto } from "../framework/cameraPhoto";
import { Button } from "ui/Scripts/source/components/framework/button";
import { UserChangePasswordDialog } from "./userChangePasswordDialog";
import { AppUtils } from "../../framework/appUtils";
import { ConfirmationDialogResult, DialogButtonType, InstantMessageUserInvitationAcceptType } from "ui/Scripts/source/models/enums";
import { IUserState } from "../../models/store/storeTypes";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IUserProfileEdit } from "../../models/user/userProfileEdit";
import { SaveAttachmentBase64 } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { IdCodeNameLogo, IIdCodeNameLogo } from "source/models/common/idCodeNameLogo";
import { SelectCompanyDialog, SelectCompanyDialogListLine } from "../framework/selectCompanyDialog";
import { EndlessList, IEndlessList } from "ui/Scripts/source/models/common/endlessList";

//UserProfileDialog
export interface IUserProfileDialogProps {
    classes?: string;
    userState: IUserState;
    userProfileEdit: IUserProfileEdit;
    onOk: () => void;
    onCancel: () => void;
}

export interface IUserProfileDialogState {
    allowedCompanies: IIdCodeNameLogo[];
    rowId: string;
    firstName: string;
    lastName: string;
    email: string;
    loginCompanyCode: string;
    emailNotificationType: number;
    pushNotificationType: number;
    instantMessageUserInvitationAcceptType: number;
    picture: string;
    file: IFileAttachment;
    fileIsPhoto: boolean;
    resetPicture: boolean;
    cameraIds: string[];
    showCamera: boolean;
    showingCamera: boolean;
    showChangePasswordDialog: boolean;
    showSelectCompanyDialog: boolean;
}

export class UserProfileDialog extends React.Component<IUserProfileDialogProps, IUserProfileDialogState> {
    constructor(props: IUserProfileDialogProps) {
        super(props);
        const user = props.userProfileEdit.user;
        this.state = {
            allowedCompanies: props.userProfileEdit.allowedCompanies,
            rowId: user.rowId,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            loginCompanyCode: user.loginCompanyCode,
            emailNotificationType: user.emailNotificationType,
            pushNotificationType: user.pushNotificationType,
            instantMessageUserInvitationAcceptType: user.instantMessageUserInvitationAcceptType,
            picture: user.picture,
            file: null,
            fileIsPhoto: false,
            resetPicture: false,
            cameraIds: [],
            showCamera: false,
            showingCamera: false,
            showChangePasswordDialog: false,
            showSelectCompanyDialog: false
        };
    }

    componentDidMount(): void {
        const obj = this;
        Base.getCameraIds().then(ids => {
            obj.setState({ cameraIds: ids });
        }).catch(() => {
            obj.setState({ cameraIds: [] });
        });
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "firstName") {
            this.setState({ firstName: value });
        } else if (name === "lastName") {
            this.setState({ lastName: value });
        } else if (name === "email") {
            this.setState({ email: value });
        } else if (name === "instantMessageUserInvitationAcceptType") {
            this.setState({ instantMessageUserInvitationAcceptType: value.toInteger() });
        }
    };

    private static getSaveDataFromState = async (props: IUserProfileDialogProps, state: IUserProfileDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.userProfileEdit.user.id);
        result.append("rowId", state.rowId);
        result.append("firstName", state.firstName);
        result.append("lastName", state.lastName);
        result.append("email", state.email);
        result.append("loginCompanyCode", state.loginCompanyCode);
        result.append("emailNotificationType", state.emailNotificationType);
        result.append("pushNotificationType", state.pushNotificationType);
        result.append("instantMessageUserInvitationAcceptType", state.instantMessageUserInvitationAcceptType);
        result.append("resetImage", state.resetPicture);
        if (!Base.isNullOrUndefined(state.file)) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file], state.fileIsPhoto ? "photo" : ""));
        }
        return result;
    };

    handleAddPictureClick = (fileList: FileList) => {
        const obj = this;
        FileAttachment.getFirstFileAttachmentFromFileList(fileList, 0).then(attachment => {
            if (Base.isNullOrUndefined(attachment)) return;
            obj.setState({ file: attachment, fileIsPhoto: false, resetPicture: false, picture: attachment.thumbnail });
        });
    };

    handleRemovePictureClick = () => {
        this.setState({ file: null, fileIsPhoto: false, resetPicture: true, picture: "" });
    };

    handleAddPhotoClick = () => {
        if (!this.hasCameras()) return;
        this.setState({ showCamera: true });
    };

    dataChanged = (): boolean => {
        const state = this.state;
        const user = this.props.userProfileEdit.user;
        return state.firstName !== user.firstName ||
            state.lastName !== user.lastName ||
            state.email !== user.email ||
            state.loginCompanyCode !== user.loginCompanyCode ||
            state.emailNotificationType !== user.emailNotificationType ||
            state.pushNotificationType !== user.pushNotificationType ||
            state.resetPicture || state.file !== null;
    };

    hasCameras = (): boolean => {
        const state = this.state;
        return state.cameraIds && state.cameraIds.length > 0;
    };

    handleCameraStreamAvailable = () => {
        this.setState({ showingCamera: true });
    };

    handleCameraPhotoOk = (dataUrl: string) => {
        if (Base.isNullOrEmpty(dataUrl)) {
            this.setState({ showCamera: false, showingCamera: false });
            return;
        }
        FileAttachment.photoToFileAttachment(dataUrl, 0).then(attachment => {
            if (Base.isNullOrUndefined(attachment)) return;
            this.setState({ file: attachment, fileIsPhoto: true, resetPicture: false, picture: attachment.thumbnail, showCamera: false, showingCamera: false });
        });
    };

    handleCameraPhotoCancel = (permissionDenied: boolean) => {
        if (!permissionDenied) {
            this.setState({ showCamera: false, showingCamera: false });
        } else {
            this.setState({ showCamera: false, showingCamera: false, cameraIds: [] });
        }
    };

    handleOnChangePassword = () => {
        this.setState({
            showChangePasswordDialog: true
        });
    };

    handleUserChangePasswordDialogOk = (rowId: string) => {
        this.setState({
            showChangePasswordDialog: false,
            rowId
        });
    };

    handleUserChangePasswordDialogCancel = () => {
        this.setState({
            showChangePasswordDialog: false
        });
    };

    handleLoadCompanies = (page: number): Promise<IEndlessList<IIdCodeNameLogo>> => {
        return new Promise<IEndlessList<IIdCodeNameLogo>>((resolve, reject) => {
            const result = new EndlessList<IIdCodeNameLogo>(null, IdCodeNameLogo);
            result.items = this.state.allowedCompanies;
            result.page = 1;
            result.hasMore = false;
            resolve(result);
        });
    };

    handleSelectCompanyDialogOk = (id: string, code: string) => {
        this.setState({
            loginCompanyCode: code,
            showSelectCompanyDialog: false
        });
    };

    handleSelectCompanyDialogCancel = () => {
        this.setState({
            showSelectCompanyDialog: false
        });
    };

    saveUserProfile = async () => {
        if (!UserProfileDialog.validate(this.state)) return;
        const saveData = await UserProfileDialog.getSaveDataFromState(this.props, this.state);
        saveData.append("changed", this.dataChanged());
        //Call server
        const result = await AppUtils.callService(() => userService.saveUserProfile(saveData.obj));
        if (!result) return;
        await AppUtils.callService(() => tokenService.refreshAuthentication(true));
        this.props.onOk();
    };

    handleCancelClick = async () => {
        const props = this.props;
        const cancelResult = await AppUtils.cancelBool(this.dataChanged());
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.saveUserProfile();
        } else {
            props.onCancel();
        }
    };

    private static validate = (state: IUserProfileDialogState): boolean => {
        if (Base.isNullOrEmpty(state.firstName)) {
            AppUtils.showErrorMessage(Translations.FirstNameMustBeDefined);
            return false;
        }
        if (Base.isNullOrEmpty(state.lastName)) {
            AppUtils.showErrorMessage(Translations.LastNameMustBeDefined);
            return false;
        }
        if (!Base.isValidEmailAddress(state.email)) {
            AppUtils.showErrorMessage(Translations.EmailAddressMustBeValid);
            return false;
        }
        return true;
    };

    handleOkClick = () => {
        this.saveUserProfile();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const user = this.props.userProfileEdit.user;
        const dialogClasses = "userProfileDialog" + (props.classes ? " " + props.classes : "");
        const canShowCamera = this.hasCameras();
        const showCamera = canShowCamera && state.showCamera;
        const showingCamera = showCamera && state.showingCamera;
        const allowedCompany = state.allowedCompanies.find((company) => company.code === state.loginCompanyCode) ?? state.allowedCompanies[0];
        return (
            <Dialog
                classes={dialogClasses}
                title={Translations.UserProfile}
                body={
                    <div>
                        <div className="row">
                            <div className="col-12">
                                <label className="control-label">{Translations.Picture}</label>
                                <ToolAddFileButton
                                    tooltip={Translations.Browse}
                                    fileInputName="picture"
                                    classes={"round mono add right"}
                                    onChange={this.handleAddPictureClick}
                                />
                                {canShowCamera &&
                                    <ToolButton
                                        tooltip={Translations.AddPhoto}
                                        classes={"round mono camera right"}
                                        onClick={this.handleAddPhotoClick}
                                    />
                                }
                                <ToolButton
                                    tooltip={Translations.Remove}
                                    classes={"round mono delete right"}
                                    onClick={this.handleRemovePictureClick}
                                />
                                <div className="pictureContainer form-group">
                                    <div className="largeIconCont">
                                        <AvatarImage
                                            classes="large"
                                            name={state.lastName + " " + state.firstName}
                                            image={state.picture}
                                        />
                                    </div>
                                    <div className="largeIconContRight">
                                        <input className="form-control" type="text" name="code" title={Translations.UserName} readOnly={true} disabled={true} value={user.code} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group required">
                                    <label className="control-label">{Translations.FirstName}</label>
                                    <input className="form-control" type="text" name="firstName" title={Translations.FirstName} maxLength={50} readOnly={false} disabled={false} value={state.firstName} required={true} onChange={this.handleChange} />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group required">
                                    <label className="control-label">{Translations.LastName}</label>
                                    <input className="form-control" type="text" name="lastName" title={Translations.LastName} maxLength={50} readOnly={false} disabled={false} value={state.lastName} required={true} onChange={this.handleChange} />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="control-label">{Translations.EmailAddress}</label>
                                    <input className="form-control" type="text" name="email" title={Translations.EmailAddress} maxLength={50} readOnly={false} disabled={false} value={state.email} required={true} onChange={this.handleChange} />
                                </div>
                            </div>
                        </div>
                        {state.allowedCompanies.length > 1 &&
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group roWhite">
                                        <label className="control-label">{Translations.LoginCompany}</label>
                                        <SelectCompanyDialogListLine
                                            classes="form-control companyLine"
                                            item={allowedCompany}
                                            selected={false}
                                            onClick={(id: string) => { this.setState({ showSelectCompanyDialog: true }); }}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="control-label">{Translations.NotificationSettings}</label>
                                    <CheckBox
                                        title={ Translations.EmailNotificationInUse }
                                        checked={ state.emailNotificationType > 0}
                                        onClickBoolean={(checked: boolean) => {
                                            this.setState({ emailNotificationType: checked ? 1 : 0 });
                                        }}
                                    />
                                    <CheckBox
                                        title={Translations.PushNotificationInUse}
                                        checked={state.pushNotificationType > 0}
                                        onClickBoolean={(checked: boolean) => {
                                            this.setState({ pushNotificationType: checked ? 1 : 0 });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {props.userState.canUseInstantMessages &&
                            <div className="row">
                                <div className="col-12">
                                    <div className="form-group">
                                        <label className="control-label">{Translations.InstantMessageSettings}</label>
                                        <select className="form-control" name="instantMessageUserInvitationAcceptType" title={Translations.InstantMessageSettings} value={state.instantMessageUserInvitationAcceptType.toString(10)} onChange={this.handleChange}>
                                            <option key={InstantMessageUserInvitationAcceptType.CannotInvite} value={InstantMessageUserInvitationAcceptType.CannotInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCannotInvite}</option>
                                            <option key={InstantMessageUserInvitationAcceptType.CanInvite} value={InstantMessageUserInvitationAcceptType.CanInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCanInvite}</option>
                                            <option key={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation} value={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeAutoAcceptInvitation}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        }
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group">
                                    <Button
                                        classes={"changePassword"}
                                        buttonType={DialogButtonType.Default}
                                        tooltip={Translations.ChangePassword}
                                        title={<span><span className="icon lock"></span>{Translations.ChangePassword}</span>}
                                        onClick={this.handleOnChangePassword}
                                    />
                                </div>
                            </div>
                        </div>
                        {state.showChangePasswordDialog &&
                            <UserChangePasswordDialog
                                minPasswordLength={props.userProfileEdit.minPasswordLength}
                                rowId={state.rowId}
                                onOk={this.handleUserChangePasswordDialogOk}
                                onCancel={this.handleUserChangePasswordDialogCancel}
                            />
                        }
                        {this.state.showSelectCompanyDialog &&
                            <SelectCompanyDialog
                                title={Translations.SelectCompany}
                                selectedId={allowedCompany?.id ?? ""}
                                loadCompanies={this.handleLoadCompanies}
                                onOk={this.handleSelectCompanyDialogOk}
                                onCancel={this.handleSelectCompanyDialogCancel}
                            />
                        }
                        {showCamera &&
                            <CameraPhoto
                                cameraIds={state.cameraIds}
                                cameraStreamAvailable={showingCamera}
                                onCameraStreamAvailable={this.handleCameraStreamAvailable}
                                onOkClick={this.handleCameraPhotoOk}
                                onCancelClick={this.handleCameraPhotoCancel}
                            />
                        }
                    </div>
                }
                buttons={[{ title: Translations.Save, buttonType: DialogButtonType.Primary, onClick: this.handleOkClick }, { title: Translations.Cancel, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }]}
                onClose={this.handleCancelClick}/>
        );
    }
}